
import { Review } from "@/types/review";

// Location mappings for English and Chinese terms
const LOCATION_MAPPINGS: Record<string, string[]> = {
  // Canada - Major Cities
  "calgary": ["calgary", "卡加利", "卡尔加里"],
  "toronto": ["toronto", "多倫多", "多伦多"],
  "vancouver": ["vancouver", "溫哥華", "温哥华"],
  "montreal": ["montreal", "蒙特利爾", "蒙特利尔"],
  "ottawa": ["ottawa", "渥太華", "渥太华"],
  "edmonton": ["edmonton", "愛民頓", "埃德蒙顿"],
  "winnipeg": ["winnipeg", "溫尼伯", "温尼伯"],
  "quebec": ["quebec", "魁北克", "魁北克"],
  "hamilton": ["hamilton", "漢密爾頓", "汉密尔顿"],
  "kitchener": ["kitchener", "基奇納", "基奇纳"],
  "london": ["london", "倫敦", "伦敦"],
  "halifax": ["halifax", "哈利法克斯", "哈利法克斯"],
  "victoria": ["victoria", "維多利亞", "维多利亚"],
  "saskatoon": ["saskatoon", "薩斯卡通", "萨斯卡通"],
  "regina": ["regina", "里賈納", "里贾纳"],
  
  // Canada - Provinces/Territories
  "alberta": ["alberta", "ab", "亞伯達", "阿尔伯塔"],
  "british columbia": ["british columbia", "bc", "英屬哥倫比亞", "不列颠哥伦比亚"],
  "ontario": ["ontario", "on", "安大略", "安大略"],
  "quebec province": ["quebec", "qc", "魁北克省", "魁北克省"],
  "manitoba": ["manitoba", "mb", "曼尼托巴", "马尼托巴"],
  "saskatchewan": ["saskatchewan", "sk", "薩斯喀徹溫", "萨斯喀彻温"],
  "nova scotia": ["nova scotia", "ns", "新斯科舍", "新斯科舍"],
  "new brunswick": ["new brunswick", "nb", "新不倫瑞克", "新不伦瑞克"],
  "newfoundland": ["newfoundland", "nl", "紐芬蘭", "纽芬兰"],
  "prince edward island": ["prince edward island", "pei", "愛德華王子島", "爱德华王子岛"],
  "northwest territories": ["northwest territories", "nt", "西北地區", "西北地区"],
  "nunavut": ["nunavut", "nu", "努納武特", "努纳武特"],
  "yukon": ["yukon", "yt", "育空", "育空"],
  
  // Taiwan - Major Cities
  "taipei": ["taipei", "台北", "臺北"],
  "taichung": ["taichung", "台中", "臺中"],
  "kaohsiung": ["kaohsiung", "高雄", "高雄"],
  "tainan": ["tainan", "台南", "臺南"],
  "taoyuan": ["taoyuan", "桃園", "桃园"],
  "hsinchu": ["hsinchu", "新竹", "新竹"],
  
  // Common terms
  "canada": ["canada", "加拿大", "加拿大"],
  "taiwan": ["taiwan", "台灣", "台湾", "中華民國", "中华民国"],
};

// Create reverse mapping for efficient lookup
const REVERSE_LOCATION_MAPPING: Record<string, string> = {};
Object.entries(LOCATION_MAPPINGS).forEach(([canonical, variations]) => {
  variations.forEach(variation => {
    REVERSE_LOCATION_MAPPING[variation.toLowerCase()] = canonical;
  });
});

export const getLocationVariations = (term: string): string[] => {
  const normalizedTerm = term.toLowerCase();
  const canonical = REVERSE_LOCATION_MAPPING[normalizedTerm];
  
  if (canonical) {
    return LOCATION_MAPPINGS[canonical] || [term];
  }
  
  return [term];
};

export const normalizeSearchTerm = (term: string): string => {
  const normalizedTerm = term.toLowerCase().trim();
  return REVERSE_LOCATION_MAPPING[normalizedTerm] || normalizedTerm;
};

export const searchReviews = (reviews: Review[], searchTerm: string): Review[] => {
  if (!searchTerm.trim()) {
    return reviews;
  }

  const normalizedSearchTerm = searchTerm.toLowerCase().trim();
  const locationVariations = getLocationVariations(normalizedSearchTerm);

  return reviews.filter(review => {
    // Search in multiple fields with location variations
    const searchFields = [
      review.community_name,
      review.address_detail,
      review.country,
      review.region,
      review.landlord_name,
      review.comment
    ];

    return searchFields.some(field => {
      if (!field) return false;
      const fieldValue = field.toLowerCase();
      
      // Check exact match first
      if (fieldValue.includes(normalizedSearchTerm)) {
        return true;
      }
      
      // Check location variations
      return locationVariations.some(variation => 
        fieldValue.includes(variation.toLowerCase())
      );
    });
  });
};

// Alias for the function name used in Reviews.tsx
export const filterReviewsBySearch = searchReviews;

export const getUniqueLocations = (reviews: Review[]): string[] => {
  const locations = new Set<string>();
  
  reviews.forEach(review => {
    if (review.community_name) {
      locations.add(review.community_name);
    }
    if (review.address_detail) {
      locations.add(review.address_detail);
    }
    if (review.region) {
      locations.add(review.region);
    }
  });
  
  return Array.from(locations).sort();
};
